import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Trash2, UserRound, Clapperboard, ClipboardList, Printer, Download, Pencil, Loader2, ChevronLeft, ChevronRight, AlertTriangle, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { sortScenes } from "../lib/scenes";
import ProjectHeader from "../components/ProjectHeader";
import AssignmentDialog from "../components/AssignmentDialog";
import FlagButton from "../components/FlagButton";
import VisibilityPicker from "../components/VisibilityPicker";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import ConfirmDialog from "../components/ConfirmDialog";
import { useAuth } from "../context/AuthContext";
import { useCaptureContext } from "../context/CaptureContext";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { entryImageSrc, entryThumbSrc, mediaUrl } from "../lib/media";
// Iter-158.13.2.20f.33c — Allergy safety indicator on the
// individual continuity-entry detail view. Reuses the existing
// AllergyBadge presentation + `useProjectAllergyMap` reader
// (character-level, no scene coupling) so tapping the badge
// navigates to the source allergy note on CharacterDetail. No
// Allergy data is copied into media/state-entry records.
import AllergyBadge from "../components/AllergyBadge";
import { useProjectAllergyMap } from "../lib/allergy";

function labelFor(entry) {
    // iter-50: state-label is no longer part of the UI vocabulary.
    // This helper now only generates an accessibility-friendly `alt`
    // fallback so the image's alt attribute is never empty.
    if (!entry) return "image";
    if (entry.note) return entry.note.slice(0, 80);
    if (entry.mode === "onset") return "On Set capture";
    if (entry.mode === "prep") return "Fittings capture";
    return "capture";
}

function formatFullTs(iso) {
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch {
        return iso;
    }
}

// Basic HTML escape so note / headings with <, >, & render safely in the print window.
function esc(value) {
    if (value == null) return "";
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

/**
 * Iter-87 — produce a printable view via a hidden iframe (NOT a
 * popup window). Pop-ups block on iPad Safari / many mobile browsers
 * and `document.write` racing with the image load was the root
 * cause of the previously-reported "blank print screen" bug.
 *
 * The iframe approach:
 * 1. Mount an off-screen iframe, hand it the rendered HTML.
 * 2. Wait for the embedded <img> to finish loading (success OR
 *    error → still print so the user sees the metadata).
 * 3. Call `iframe.contentWindow.print()` AFTER the image is decoded
 *    and the iframe's onload has fired.
 * 4. After the print dialog closes (or is cancelled) tear the
 *    iframe down so the user is left on the original Image Detail.
 */
function openPrintWindow({ entry, project, scene, character, label, projectId, apiBase }) {
    // Prefer the highest-quality variant for print: GridFS original when
    // available, then display, then legacy inline data URL.
    const printSrc = entry.media_id
        ? mediaUrl(projectId, entry.media_id, "original", { apiBase })
        : (entry.image_data || "");
    const characterLine = character?.name
        ? character.actor
            ? `${character.name} · ${character.actor}`
            : character.name
        : null;
    const sceneRef =
        scene?.scene_number && scene?.heading
            ? `Sc ${scene.scene_number} — ${scene.heading}`
            : scene?.scene_number
            ? `Sc ${scene.scene_number}`
            : scene?.heading || null;
    const rows = [
        project?.name ? { label: "Project", value: project.name } : null,
        sceneRef ? { label: "Scene", value: sceneRef } : null,
        // Iter-120 — surface the scene-level Look in the metadata
        // sheet right under the Scene row so the user reads:
        // "Sc 1 — INT. CAFE — DAY / Scene look: Rain".
        scene?.look ? { label: "Scene look", value: scene.look } : null,
        characterLine ? { label: "Character", value: characterLine } : null,
        { label: "Mode", value: entry.mode === "onset" ? "On Set" : "Fittings" },
        label ? { label: "State", value: label } : null,
        entry.look ? { label: "Look", value: entry.look } : null,
        entry.note ? { label: "Notes", value: entry.note } : null,
        entry.created_by_name
            ? { label: "Captured by", value: entry.created_by_name }
            : null,
        entry.created_at
            ? { label: "Timestamp", value: formatFullTs(entry.created_at) }
            : null,
    ].filter(Boolean);

    const title = `${project?.name || "OnSetFlow"} — ${
        scene?.scene_number ? "Scene " + scene.scene_number : "Entry"
    }`;

    const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${esc(title)}</title>
<style>
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #111;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  main { padding: 24px; max-width: 720px; margin: 0 auto; }
  header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    border-bottom: 1px solid #ddd;
    padding-bottom: 12px;
    margin-bottom: 20px;
  }
  h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }
  .brand { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: #888; }
  .image-wrap {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #f4f4f4;
    border: 1px solid #e5e5e5;
    padding: 12px;
    border-radius: 4px;
  }
  .image-wrap img {
    max-width: 100%;
    max-height: 70vh;
    height: auto;
    display: block;
  }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  th, td {
    text-align: left;
    padding: 8px 0;
    vertical-align: top;
    border-bottom: 1px solid #eee;
    font-size: 13px;
  }
  th {
    width: 140px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 11px;
  }
  td { color: #111; white-space: pre-wrap; word-break: break-word; }
  footer {
    margin-top: 24px;
    padding-top: 12px;
    border-top: 1px solid #ddd;
    font-size: 10px;
    color: #888;
    display: flex;
    justify-content: space-between;
  }
  @media print {
    @page { margin: 18mm; }
    body { padding: 0; }
    main { padding: 0; }
    footer { page-break-before: avoid; }
    .image-wrap { background: transparent; border: none; padding: 0; }
    .image-wrap img { max-height: none; }
  }
</style>
</head>
<body>
  <main>
    <header>
      <h1>${esc(title)}</h1>
      <span class="brand">OnSetFlow</span>
    </header>
    <div class="image-wrap">
      <img id="onsetflow-print-img" src="${esc(printSrc)}" alt="${esc(label || "Scene image")}" />
    </div>
    <table>
      <tbody>
        ${rows
            .map(
                (r) =>
                    `<tr><th>${esc(r.label)}</th><td>${esc(r.value)}</td></tr>`,
            )
            .join("")}
      </tbody>
    </table>
    <footer>
      <span>Printed ${esc(new Date().toLocaleString())}</span>
      <span>${esc(title)}</span>
    </footer>
  </main>
</body>
</html>`;

    // Build an off-screen iframe. We deliberately use srcdoc (rather
    // than document.write) — srcdoc gives us a properly-loaded
    // document and avoids the document.write race that blanked the
    // popup version on iPad Safari.
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("data-testid", "image-detail-print-frame");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    iframe.srcdoc = html;
    document.body.appendChild(iframe);

    let printed = false;
    const cleanup = () => {
        // Tiny defer so the print dialog has a chance to register the
        // print job before we yank the iframe out of the DOM. On
        // some browsers removing the iframe synchronously after
        // .print() cancels the dialog.
        setTimeout(() => {
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }, 1500);
    };

    const fireOnce = () => {
        if (printed) return;
        printed = true;
        try {
            const w = iframe.contentWindow;
            if (!w) {
                // Iframe was detached before print could fire — fall
                // back to printing the host tab so the user gets
                // SOMETHING rather than silence.
                window.print();
                return;
            }
            w.focus();
            w.print();
        } catch (e) {
            // Final fallback: print the host tab.
            window.print();
        } finally {
            cleanup();
        }
    };

    iframe.addEventListener("load", () => {
        const doc = iframe.contentDocument;
        const img = doc?.getElementById("onsetflow-print-img");
        if (!img) {
            // No image (legacy entry without media) — just print.
            fireOnce();
            return;
        }
        if (img.complete && img.naturalWidth > 0) {
            fireOnce();
        } else {
            img.addEventListener("load", fireOnce, { once: true });
            // On error we still print so the user sees the metadata
            // sheet — better than a totally blank page.
            img.addEventListener("error", fireOnce, { once: true });
            // Hard cap: don't wait forever on a stalled connection.
            setTimeout(fireOnce, 5000);
        }
    });
}

export default function ImageDetail() {
    const { projectId, entryId } = useParams();
    const navigate = useNavigate();
    const routeBase = useRouteBase();
    const apiBase = useApiBase();
    const location = useLocation();
    const { user } = useAuth();

    const [project, setProject] = useState(null);
    const [entry, setEntry] = useState(null);
    const [character, setCharacter] = useState(null);
    const [scene, setScene] = useState(null);
    // Iter-118 — full grouping for the underlying media. ALL state_entry
    // rows that share this image's media_id contribute their (scene_id,
    // character_id) so the user sees every link as a chip and can edit
    // the whole grouping in one dialog rather than per-row.
    const [groupingEntries, setGroupingEntries] = useState([]);
    const [linkedScenes, setLinkedScenes] = useState([]);  // [{id, scene_number, heading, episode}]
    const [linkedCharacters, setLinkedCharacters] = useState([]);  // [{id, name, actor, character_number, ...}]
    // Iter-158.13.2.20f.33c — Character-level allergy map for the
    // linked-character chips. Reuses the project-wide reader — no
    // new endpoint. Renders nothing when a character has no
    // allergy record. Badge navigation targets the source
    // allergy note on CharacterDetail (`#allergy`).
    const { byCharacterId: allergyByCharacterId } = useProjectAllergyMap(
        apiBase,
        projectId,
    );
    const [reloadKey, setReloadKey] = useState(0);
    const [error, setError] = useState("");
    // Iter-157E P1(8) — track WHY the entry failed to load so we
    // can route to permission-specific copy when the backend
    // returns 403, instead of the generic "Unable to load image"
    // headline (which read like a system failure for what is
    // actually a calm "you don't have access" path).
    const [errorStatus, setErrorStatus] = useState(0);
    // Inline note editing — independent from the assignment dialog so users
    // can fix typos / add detail without re-walking the picker UI.
    const [editingNote, setEditingNote] = useState(false);
    const [noteDraft, setNoteDraft] = useState("");
    const [savingNote, setSavingNote] = useState(false);
    // Iter-157D — continuity-entry visibility (parity with notes).
    const [visibilityDraft, setVisibilityDraft] = useState("everyone");
    // iter-50: full reassignment (mode + scene + character) re-uses the
    // existing AssignmentDialog in edit mode. Opens from the "Edit
    // assignment" pencil button next to the Scene/Character card.
    const [reassignOpen, setReassignOpen] = useState(false);
    // Fullscreen lightbox toggle — tapping the image opens a true
    // edge-to-edge view with a clear close button. Esc / tap closes.
    const [fullscreen, setFullscreen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!fullscreen) return;
        const onKey = (e) => {
            if (e.key === "Escape") setFullscreen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [fullscreen]);

    // Sibling list for left/right navigation. Grouping (per spec):
    //   - Assigned + scene_id + character_id  → "this character in this scene"
    //   - Assigned, no scene                  → "this character" (Fittings)
    //   - Unassigned                          → all unassigned in project
    // Sorted oldest → newest so swiping right moves chronologically forward.
    const [siblings, setSiblings] = useState([]);
    // iter-72 PART 2/3 state — declared BEFORE the siblings useEffect
    // because that effect's dep array references `siblingRefreshKey`.
    // Hoisting `const` doesn't help here: the dep-array literal is
    // evaluated at render time and would hit the TDZ otherwise.
    const [siblingRefreshKey, setSiblingRefreshKey] = useState(0);
    const [highlightedEntryId, setHighlightedEntryId] = useState(null);

    useEffect(() => {
        // Iter-118.3 — each fetch independent. Only the entry fetch
        // is fatal: without it there's nothing to render. Project +
        // characters fetches degrade silently (header still shows the
        // name once it lands; chip names backfill once characters
        // arrive). This eliminates the "blank Image Detail" regression
        // where a transient /characters timeout would block the entire
        // viewer behind the loading skeleton forever.
        let cancelled = false;

        api.get(`${apiBase}/${projectId}`)
            .then(({ data }) => {
                if (!cancelled) setProject(data);
            })
            .catch(() => {
                /* non-fatal — header just shows "Project" */
            });

        api.get(`${apiBase}/${projectId}/characters`)
            .then(({ data: charList }) => {
                if (cancelled) return;
                // Stash the list — the resolver effect below matches
                // entry.character_id against it once both have arrived.
                setCharactersList(charList);
            })
            .catch(() => {
                /* non-fatal */
            });

        api.get(`${apiBase}/${projectId}/state-entries/${entryId}`)
            .then(({ data: e }) => {
                if (cancelled) return;
                setEntry(e);
                setError("");
                setErrorStatus(0);
                if (e.scene_id) {
                    // Iter-84 — scene may have been deleted/omitted by
                    // the time we open this image. Isolate the fetch
                    // so a 404 here doesn't blank the page.
                    api.get(`${apiBase}/${projectId}/scenes/${e.scene_id}`)
                        .then(({ data: sc }) => {
                            if (!cancelled) setScene(sc);
                        })
                        .catch(() => {
                            if (!cancelled) setScene(null);
                        });
                } else {
                    setScene(null);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(formatApiError(err));
                    setErrorStatus(err?.response?.status || 0);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [projectId, entryId, reloadKey]);

    // Iter-118.3 — characters list is hoisted into its own state so
    // the focal-character resolver can run BOTH on entry-arrival AND
    // on characters-arrival regardless of which lands first.
    const [charactersList, setCharactersList] = useState([]);

    useEffect(() => {
        if (!entry) return;
        if (!charactersList || charactersList.length === 0) return;
        const matched = charactersList.find((c) => c.id === entry.character_id) || null;
        setCharacter(matched);
    }, [entry, charactersList]);

    // Iter-118 — fetch every state_entry that shares this image's
    // media_id and derive the unique scene + character chip lists.
    // Surfaces the FULL grouping on Image Detail so users can see at
    // a glance every scene and character the photo is linked to.
    // Falls back to the single-entry pair when the entry has no
    // media_id (legacy inline images).
    useEffect(() => {
        if (!entry) return;
        let cancelled = false;
        (async () => {
            try {
                if (!entry.media_id) {
                    setGroupingEntries([entry]);
                    return;
                }
                const { data } = await api.get(
                    `${apiBase}/${projectId}/state-entries`,
                    { params: { media_id: entry.media_id } },
                );
                if (cancelled) return;
                setGroupingEntries(Array.isArray(data) ? data : []);
            } catch {
                if (!cancelled) setGroupingEntries([entry]);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, entry?.media_id, entry?.id, entry?.scene_id, entry?.character_id]);

    // Once the grouping arrives, hydrate scene + character chip lists.
    // We pull characters from the cached `/characters` list (cheap)
    // and scenes one-by-one (rare path; usually 1–3 scenes per image).
    useEffect(() => {
        if (!entry || groupingEntries.length === 0) {
            setLinkedScenes([]);
            setLinkedCharacters([]);
            return;
        }
        let cancelled = false;
        (async () => {
            const sceneIdSet = Array.from(
                new Set(groupingEntries.map((e) => e.scene_id).filter(Boolean)),
            );
            const charIdSet = Array.from(
                new Set(groupingEntries.map((e) => e.character_id).filter(Boolean)),
            );
            try {
                const { data: chars } = await api.get(
                    `${apiBase}/${projectId}/characters`,
                );
                if (cancelled) return;
                setLinkedCharacters(
                    chars.filter((c) => charIdSet.includes(c.id)),
                );
            } catch {
                /* keep existing */
            }
            const sceneFetches = sceneIdSet.map((id) =>
                api
                    .get(`${apiBase}/${projectId}/scenes/${id}`)
                    .then((r) => r.data)
                    .catch(() => null),
            );
            const resolved = await Promise.all(sceneFetches);
            if (cancelled) return;
            // Iter-129 — canonical scene ordering everywhere.
            setLinkedScenes(sortScenes(resolved.filter(Boolean)));
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, groupingEntries.length, entry?.media_id]);

    // Fetch the sibling list AFTER the entry has loaded so we know which
    // group it belongs to. The list backs the swipe / arrow navigation.
    useEffect(() => {
        if (!entry) return;
        let cancelled = false;
        (async () => {
            try {
                const params = {};
                if (entry.assigned === false) {
                    params.assigned = false;
                } else {
                    if (entry.character_id) params.character_id = entry.character_id;
                    if (entry.scene_id) params.scene_id = entry.scene_id;
                }
                const { data } = await api.get(
                    `${apiBase}/${projectId}/state-entries`,
                    { params }
                );
                if (cancelled) return;
                // Backend returns newest-first; flip for chronological swipe.
                const sorted = [...data].sort(
                    (a, b) => new Date(a.created_at) - new Date(b.created_at)
                );
                setSiblings(sorted);
            } catch {
                // Non-fatal: arrows simply won't appear.
                if (!cancelled) setSiblings([]);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        projectId,
        entry?.id,
        entry?.assigned,
        entry?.character_id,
        entry?.scene_id,
        siblingRefreshKey,
    ]);

    const { prevId, nextId, position, total } = useMemo(() => {
        if (!entry || siblings.length === 0) {
            return { prevId: null, nextId: null, position: 0, total: 0 };
        }
        const idx = siblings.findIndex((s) => s.id === entry.id);
        if (idx === -1) return { prevId: null, nextId: null, position: 0, total: siblings.length };
        return {
            prevId: idx > 0 ? siblings[idx - 1].id : null,
            nextId: idx < siblings.length - 1 ? siblings[idx + 1].id : null,
            position: idx + 1,
            total: siblings.length,
        };
    }, [entry, siblings]);

    const goPrev = () => {
        // Preserve the original `state` (backHref / backLabel) when
        // swiping to a sibling so the in-page Back link still routes the
        // user to where they originally entered the viewer (Recent
        // Activity, character page, etc.) — not back to whatever sibling
        // they were viewing previously.
        if (prevId)
            navigate(`${routeBase}/${projectId}/state-entries/${prevId}`, {
                replace: false,
                state: location.state,
            });
    };
    const goNext = () => {
        if (nextId)
            navigate(`${routeBase}/${projectId}/state-entries/${nextId}`, {
                replace: false,
                state: location.state,
            });
    };
    // iter-70 PART 6 — let the new thumbnail strip jump to any
    // sibling in one tap. Keeps the same `state` preservation as
    // goPrev / goNext so the Back link continues to point at the
    // user's true entry surface (Recent Activity, character page,
    // scene block) rather than the previous thumbnail.
    const goToSibling = (id) => {
        if (!id || id === entryId) return;
        navigate(`${routeBase}/${projectId}/state-entries/${id}`, {
            replace: false,
            state: location.state,
        });
    };

    // iter-70 PART 6 — keep the active thumbnail visible inside the
    // horizontal strip. We stamp the active thumb with
    // `data-active="true"` and scroll it into view (no animation —
    // matches the rest of the app's instant-paint policy) whenever the
    // current entry changes.
    const thumbStripRef = useRef(null);
    useEffect(() => {
        if (!thumbStripRef.current || !entry) return;
        const el = thumbStripRef.current.querySelector(
            `[data-thumb-id="${entry.id}"]`,
        );
        if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({
                behavior: "auto",
                block: "nearest",
                inline: "center",
            });
        }
    }, [entry?.id, siblings.length]);

    // iter-72 PART 2 — refresh after capture.
    // When the user captures from ImageDetail, AssignmentDialog
    // emits `onsetflow:state-entry-saved` with the full list of
    // newly-created entries. We refetch the sibling list (so the
    // thumbnail strip + position badge include the new entry) and,
    // when at least one new entry belongs to the current group,
    // navigate to it so the user immediately sees the image they
    // just took. Highlights the freshly-saved thumb via
    // `highlightedEntryId` for ~2.4s. (state declared above.)
    useEffect(() => {
        const onSavedEvent = (ev) => {
            if (!entry) return;
            const detail = ev?.detail || {};
            const created = Array.isArray(detail.entries) ? detail.entries : [];
            const matches = created.filter((e) => {
                if (!e) return false;
                if (entry.assigned === false) return e.assigned === false;
                if (entry.scene_id && e.scene_id !== entry.scene_id) return false;
                if (entry.character_id && e.character_id !== entry.character_id) return false;
                return true;
            });
            setSiblingRefreshKey((k) => k + 1);
            const target = matches[matches.length - 1];
            if (target?.id && target.id !== entry.id) {
                setHighlightedEntryId(target.id);
                navigate(`${routeBase}/${projectId}/state-entries/${target.id}`, {
                    replace: false,
                    state: location.state,
                });
            } else if (target?.id) {
                setHighlightedEntryId(target.id);
            }
        };
        window.addEventListener("onsetflow:state-entry-saved", onSavedEvent);
        return () => window.removeEventListener("onsetflow:state-entry-saved", onSavedEvent);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entry, projectId]);

    // Drop the highlight after 2.4s so the amber pulse on the
    // freshly-added thumb doesn't linger forever.
    useEffect(() => {
        if (!highlightedEntryId) return;
        const t = setTimeout(() => setHighlightedEntryId(null), 2400);
        return () => clearTimeout(t);
    }, [highlightedEntryId]);

    // Keyboard arrows on desktop. Skip if the user is typing in the note
    // textarea or any other input — we don't want left-arrow to nuke their
    // cursor position.
    useEffect(() => {
        const onKey = (ev) => {
            const tag = (ev.target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || ev.target?.isContentEditable) return;
            if (ev.key === "ArrowLeft") goPrev();
            else if (ev.key === "ArrowRight") goNext();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prevId, nextId]);

    // Touch swipe on the image area. Threshold of 50px guards against
    // accidental taps; vertical-dominated gestures are ignored so users can
    // still scroll the page with a finger on the photo.
    const touchStartRef = useRef({ x: 0, y: 0, t: 0 });
    const onTouchStart = (e) => {
        const t = e.changedTouches[0];
        touchStartRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    };
    const onTouchEnd = (e) => {
        const t = e.changedTouches[0];
        const start = touchStartRef.current;
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        const dt = Date.now() - start.t;
        if (dt > 800) return; // slow drag isn't a swipe
        if (Math.abs(dx) < 50) return;
        if (Math.abs(dx) < Math.abs(dy)) return; // vertical scroll wins
        if (dx < 0) goNext();
        else goPrev();
    };

    const mine = entry && user && entry.created_by === user.id;
    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    // Iter-156A.5 Phase 5 — archive read-only enforcement on the FE.
    const isArchived = Boolean(project?.archived);
    const canManage = (isOwner || isAdmin) && !isArchived;
    const canDelete = (mine || isOwner || isAdmin) && !isArchived;
    const capture = useCaptureContext();

    const remove = async () => {
        setDeleteOpen(false);
        setDeleting(true);
        try {
            await api.delete(`${apiBase}/${projectId}/state-entries/${entryId}`);
            navigate(-1);
        } catch (e) {
            // Iter-158.12.12 — mutation failure MUST NOT set the
            // top-level `error` banner (which reads "Unable to load
            // this image"). That banner is scoped to LOAD failures.
            // Mutation errors — including 403 permission denials —
            // surface as toasts with the backend's own copy (which
            // is now "You don't have permission to delete this
            // entry" via the updated hm_workspace/media.py copy).
            toast.error(formatApiError(e));
        } finally {
            setDeleting(false);
        }
    };

    const startEditNote = () => {
        setNoteDraft(entry?.note || "");
        setVisibilityDraft(entry?.visibility || "everyone");
        setEditingNote(true);
    };

    const cancelEditNote = () => {
        setEditingNote(false);
        setNoteDraft("");
        setVisibilityDraft(entry?.visibility || "everyone");
    };

    const saveNote = async () => {
        const trimmed = noteDraft.trim();
        const visChanged = (entry?.visibility || "everyone") !== visibilityDraft;
        // No-op only if NOTHING changed.
        if ((entry?.note || "") === trimmed && !visChanged) {
            setEditingNote(false);
            return;
        }
        setSavingNote(true);
        try {
            const patch = { note: trimmed || null };
            if (visChanged) patch.visibility = visibilityDraft;
            // Note-only PATCH — backend leaves all assignment fields
            // alone when none of mode/character_id/scene_id/state_label
            // is sent.
            const { data } = await api.patch(
                `${apiBase}/${projectId}/state-entries/${entryId}`,
                patch,
            );
            setEntry(data);
            setEditingNote(false);
            toast.success(visChanged ? "Saved" : "Note updated");
        } catch (e) {
            // Iter-158.12.12 — see comment on remove(). Note save
            // errors (403 permission-denied included) surface as a
            // toast, not the top-level LOAD error banner, so the
            // user sees "You don't have permission to edit this
            // entry" instead of the misleading "Unable to load
            // this image..." copy.
            toast.error(formatApiError(e));
        } finally {
            setSavingNote(false);
        }
    };

    // Resolve back destination from router state (origin-aware) so users
    // return to where they actually came from — Recent Activity, the
    // Unassigned bucket, a character continuity view, the flags page,
    // etc. Falls back to scene/Fittings only when the page was reached
    // via a deep link (no state). The label in the link can also be
    // customised by the originating page.
    const stateBack = location.state?.backHref;
    const backLabel = location.state?.backLabel || "Back";
    // Iter-84 — if the originating scene_id no longer resolves to a
    // live scene (deleted/omitted), don't link back into a 404. Fall
    // through to the mode landing page instead.
    const backHref =
        stateBack ||
        (entry?.scene_id && scene
            ? `${routeBase}/${projectId}/scenes/${entry.scene_id}`
            : entry?.mode === "prep"
            ? `${routeBase}/${projectId}/prep`
            : `${routeBase}/${projectId}/onset`);

    return (
        <div data-testid="image-detail-page" className="min-h-screen">
            <ProjectHeader projectName={project?.name} subtitle="Entry detail" />

            <main className="max-w-2xl mx-auto px-5 pt-5 pb-48">
                {/* Iter-136 — Removed inline "Back to project" row.
                    ProjectHeader chevron is the sole back affordance
                    across the app. The error-state still keeps its own
                    Back button (operationally useful when the entry
                    fails to load). */}

                {error ? (
                    <div
                        data-testid="image-detail-error"
                        data-error-kind={errorStatus === 403 ? "permission" : "generic"}
                        className={`rounded-xl border p-5 space-y-3 ${
                            errorStatus === 403
                                ? "border-amber-500/30 bg-amber-500/5"
                                : "border-red-500/30 bg-red-500/5"
                        }`}
                    >
                        <div className="flex items-start gap-3">
                            <AlertTriangle
                                size={18}
                                className={`mt-0.5 shrink-0 ${
                                    errorStatus === 403
                                        ? "text-amber-300"
                                        : "text-red-300"
                                }`}
                            />
                            <div>
                                <h2
                                    data-testid="image-detail-error-title"
                                    className="font-display text-lg font-bold text-white"
                                >
                                    {errorStatus === 403
                                        ? "You don\u2019t have permission to view this entry"
                                        : "Unable to load this image"}
                                </h2>
                                <p
                                    data-testid="image-detail-error-body"
                                    className="text-sm text-neutral-300 mt-1"
                                >
                                    {/* Iter-157E P1(8) — explicit
                                        permission copy. Viewers /
                                        contributors hitting a
                                        visibility-restricted entry
                                        used to see "Unable to load
                                        image. The entry may have
                                        been deleted, or there was a
                                        network hiccup" — which read
                                        like a system failure. The
                                        truth is calmer and we say so:
                                        the entry exists, your account
                                        just doesn't have access to
                                        it. Ask an admin if you need
                                        to see it. */}
                                    {errorStatus === 403
                                        ? "You do not have permission to perform this action. The entry exists, but it\u2019s scoped to a different set of roles. Ask your project Owner or an Administrator if you need access."
                                        : "The entry may have been deleted, or there was a network hiccup. Your other data is fine."}
                                </p>
                                <p
                                    data-testid="image-detail-error-detail"
                                    className={`text-xs mt-1.5 font-mono ${
                                        errorStatus === 403
                                            ? "text-amber-300/80"
                                            : "text-red-300/80"
                                    }`}
                                >
                                    {error}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {errorStatus !== 403 ? (
                                <button
                                    type="button"
                                    data-testid="image-detail-error-retry"
                                    onClick={() => {
                                        setError("");
                                        setErrorStatus(0);
                                        setEntry(null);
                                        setReloadKey((k) => k + 1);
                                    }}
                                    className="instant-tap inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground font-semibold px-4 py-2 text-sm"
                                >
                                    Retry
                                </button>
                            ) : null}
                            <Link
                                to={backHref}
                                data-testid="image-detail-error-back"
                                className="instant-tap inline-flex items-center justify-center gap-2 rounded-md bg-secondary border border-white/10 text-foreground px-4 py-2 text-sm"
                            >
                                <ArrowLeft size={14} />
                                {backLabel}
                            </Link>
                        </div>
                    </div>
                ) : !entry ? (
                    <div
                        data-testid="image-detail-loading"
                        className="h-80 rounded-xl border border-white/10 bg-card animate-pulse"
                    />
                ) : (
                    <>
                        <div
                            className="relative rounded-xl overflow-hidden border border-white/10 bg-black"
                            onTouchStart={onTouchStart}
                            onTouchEnd={onTouchEnd}
                            data-testid="image-detail-viewer"
                        >
                            <img
                                data-testid="image-detail-img"
                                src={entryImageSrc(projectId, entry, "display", apiBase)}
                                alt={labelFor(entry)}
                                onClick={() => setFullscreen(true)}
                                className="w-full h-auto max-h-[80vh] object-contain bg-black select-none cursor-zoom-in"
                                draggable={false}
                            />
                            {prevId ? (
                                <button
                                    type="button"
                                    data-testid="image-detail-prev"
                                    onClick={goPrev}
                                    aria-label="Previous image"
                                    title="Previous (←)"
                                    className="tap absolute left-2 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/55 hover:bg-black/75 text-white inline-flex items-center justify-center backdrop-blur-sm"
                                >
                                    <ChevronLeft size={22} />
                                </button>
                            ) : null}
                            {nextId ? (
                                <button
                                    type="button"
                                    data-testid="image-detail-next"
                                    onClick={goNext}
                                    aria-label="Next image"
                                    title="Next (→)"
                                    className="tap absolute right-2 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/55 hover:bg-black/75 text-white inline-flex items-center justify-center backdrop-blur-sm"
                                >
                                    <ChevronRight size={22} />
                                </button>
                            ) : null}
                            {total > 1 ? (
                                <div
                                    data-testid="image-detail-position"
                                    className="absolute bottom-2 right-2 px-2.5 py-1 rounded-full bg-black/60 text-white text-[11px] font-medium tabular-nums backdrop-blur-sm"
                                >
                                    {position} / {total}
                                </div>
                            ) : null}
                        </div>

                        {/* iter-70 PART 6 — Thumbnail strip.
                            Quick visual index of every sibling image
                            in the current group (this character × this
                            scene, this character's Fittings, or all
                            unassigned). Tap a thumbnail to jump to
                            that entry instantly. The active thumbnail
                            is highlighted with a primary ring and
                            auto-scrolled into view via
                            `thumbStripRef`. Hidden when there's only
                            one image in the group — no value at N=1. */}
                        {siblings.length > 1 ? (
                            <div
                                ref={thumbStripRef}
                                data-testid="image-detail-thumb-strip"
                                aria-label="Image thumbnails"
                                className="mt-3 -mx-1 px-1 flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory"
                                style={{ WebkitOverflowScrolling: "touch" }}
                            >
                                {siblings.map((s, idx) => {
                                    const isActive = s.id === entry.id;
                                    const isHighlighted = s.id === highlightedEntryId;
                                    // iter-72 PART 3 fix — additive class composition.
                                    // The freshly-saved thumb is BOTH the active
                                    // entry AND highlighted in the typical save
                                    // flow. With a ternary cascade the active
                                    // branch wins and the amber pulse never
                                    // shows. Compose flags additively so the
                                    // highlight ring + onset-flash keyframe take
                                    // priority during the 2.4s window — they
                                    // naturally fall back to the active ring
                                    // afterwards once highlightedEntryId clears.
                                    const ringClasses = isHighlighted
                                        ? "border-amber-300 ring-2 ring-amber-300 onset-flash"
                                        : isActive
                                            ? "border-primary ring-2 ring-primary"
                                            : "border-white/10 opacity-70 hover:opacity-100";
                                    return (
                                        <button
                                            key={s.id}
                                            type="button"
                                            data-thumb-id={s.id}
                                            data-active={isActive ? "true" : "false"}
                                            data-highlighted={isHighlighted ? "true" : "false"}
                                            data-testid={`image-detail-thumb-${s.id}`}
                                            onClick={() => goToSibling(s.id)}
                                            aria-label={`Jump to image ${idx + 1} of ${siblings.length}`}
                                            aria-current={isActive ? "true" : undefined}
                                            className={`instant-tap snap-start shrink-0 relative h-16 w-16 rounded-md overflow-hidden border ${ringClasses} bg-black`}
                                        >
                                            <img
                                                src={entryThumbSrc(projectId, s, apiBase)}
                                                alt=""
                                                loading="lazy"
                                                draggable={false}
                                                className="absolute inset-0 h-full w-full object-cover select-none"
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        ) : null}

                        <section className="mt-5 flex flex-col space-y-4">
                            <div className="order-1 flex items-center gap-2 flex-wrap">
                                <span
                                    data-testid="image-detail-mode"
                                    className={`text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full font-semibold ${
                                        entry.mode === "onset"
                                            ? "bg-primary/15 text-primary border border-primary/30"
                                            : "bg-amber-400/10 text-amber-200 border border-amber-400/30"
                                    }`}
                                >
                                    {entry.mode === "onset" ? "On Set" : "Fittings"}
                                </span>
                                {entry.not_in_script ? (
                                    <span
                                        data-testid="image-detail-not-in-script"
                                        title="This character is not part of the scripted scene. Assignment may be wrong."
                                        className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full font-semibold bg-amber-400/15 text-amber-200 border border-amber-400/30"
                                    >
                                        Not in script
                                    </span>
                                ) : null}

                                {/* Iter-124 — contextual Upload action
                                    restored. Exactly ONE button; fires
                                    an `onsetflow:capture` event with the
                                    current image's scene / character /
                                    episode / mode so CaptureFab's
                                    pipeline lands the next image in the
                                    SAME slot (iter-116b behaviour). The
                                    legacy `image-detail-capture` testid
                                    is kept so existing automation still
                                    hits the primary tap surface. */}
                                <div className="ml-auto">
                                    <button
                                        type="button"
                                        data-testid="image-detail-capture"
                                        data-action="upload"
                                        onClick={() =>
                                            window.dispatchEvent(
                                                new CustomEvent("onsetflow:capture", {
                                                    detail: {
                                                        action: "photos",
                                                        context: {
                                                            mode: entry.mode || "onset",
                                                            scene_id: entry.scene_id || undefined,
                                                            character_id: entry.character_id || undefined,
                                                            episode: scene?.episode || undefined,
                                                            look: entry.look || undefined,
                                                        },
                                                    },
                                                }),
                                            )
                                        }
                                        title="Upload another image in the same context"
                                        aria-label="Upload image"
                                        className="tap inline-flex items-center justify-center h-9 w-9 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                                    >
                                        <ImagePlus size={14} />
                                    </button>
                                </div>
                            </div>

                            <div className="order-4 rounded-xl border border-white/10 bg-card p-4 space-y-3 text-sm">
                                <div className="flex items-center justify-between">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                        Assignment
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <FlagButton
                                            projectId={projectId}
                                            entityType="state_entry"
                                            entityId={entry.id}
                                            entityLabel="image"
                                            testid="image-detail-flag"
                                        />
                                        <button
                                            type="button"
                                            data-testid="image-detail-edit-assignment"
                                            onClick={() => setReassignOpen(true)}
                                            className="tap inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] text-neutral-300 hover:text-white hover:bg-white/5"
                                        >
                                            <Pencil size={11} />
                                            Edit
                                        </button>
                                    </div>
                                </div>

                                {/* Iter-118 — Linked scenes (chip list).
                                    Surfaces every scene the underlying
                                    media is attached to so the user can
                                    see (and tap) the full grouping at a
                                    glance. Episode prefix is rendered in
                                    the chip label so e.g. Ep 1 Sc 39 vs
                                    Ep 4 Sc 39 are unambiguous. */}
                                <div data-testid="image-detail-scenes">
                                    <div className="flex items-center gap-2">
                                        <Clapperboard size={14} className="text-neutral-500" />
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                            {linkedScenes.length === 0
                                                ? entry.mode === "prep"
                                                    ? "Fittings · no scene"
                                                    : "Scenes · none"
                                                : linkedScenes.length === 1
                                                    ? "Scene"
                                                    : `Scenes · ${linkedScenes.length}`}
                                        </div>
                                    </div>
                                    {linkedScenes.length > 0 ? (
                                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                                            {linkedScenes.map((sc) => {
                                                const label = sc.episode
                                                    ? `${sc.episode}.${sc.scene_number || "—"}`
                                                    : String(sc.scene_number || "—");
                                                return (
                                                    <Link
                                                        key={sc.id}
                                                        data-testid={`image-detail-scene-chip-${sc.id}`}
                                                        to={`${routeBase}/${projectId}/scenes/${sc.id}`}
                                                        state={{
                                                            returnTo: {
                                                                path: `${routeBase}/${projectId}/state-entries/${entry.id}`,
                                                                label: "Back to image",
                                                            },
                                                        }}
                                                        className="instant-tap inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary border border-white/10 text-xs text-white hover:bg-white/10"
                                                    >
                                                        <span className="mono text-[10px] opacity-80">
                                                            {label}
                                                        </span>
                                                        <span className="truncate max-w-[160px]">
                                                            {sc.heading || "—"}
                                                        </span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    ) : entry.mode === "onset" ? (
                                        <p className="mt-1.5 text-amber-100 text-sm">
                                            Not assigned —{" "}
                                            <button
                                                type="button"
                                                data-testid="image-detail-assign-scene-link"
                                                onClick={() => setReassignOpen(true)}
                                                className="underline hover:text-white"
                                            >
                                                pick a scene
                                            </button>
                                        </p>
                                    ) : (
                                        <p className="mt-1.5 text-neutral-400 text-sm">
                                            Pre-production reference
                                        </p>
                                    )}
                                </div>

                                {/* Iter-118 — Linked characters (chip list). */}
                                <div data-testid="image-detail-characters">
                                    <div className="flex items-center gap-2">
                                        <UserRound size={14} className="text-neutral-500" />
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                            {linkedCharacters.length === 0
                                                ? "Characters · none"
                                                : linkedCharacters.length === 1
                                                    ? "Character"
                                                    : `Characters · ${linkedCharacters.length}`}
                                        </div>
                                    </div>
                                    {linkedCharacters.length > 0 ? (
                                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                                            {linkedCharacters.map((c) => {
                                                const cAllergy = allergyByCharacterId.get(c.id) || null;
                                                return (
                                                <div
                                                    key={c.id}
                                                    className="inline-flex items-center gap-1.5"
                                                >
                                                <Link
                                                    data-testid={`image-detail-character-chip-${c.id}`}
                                                    to={`${routeBase}/${projectId}/characters/${c.id}`}
                                                    state={{
                                                        returnTo: {
                                                            path: `${routeBase}/${projectId}/state-entries/${entry.id}`,
                                                            label: "Back to image",
                                                        },
                                                    }}
                                                    className="instant-tap inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary border border-white/10 text-xs text-white hover:bg-white/10"
                                                >
                                                    {c.character_number ? (
                                                        <span className="mono text-[10px] opacity-70">
                                                            #{c.character_number}
                                                        </span>
                                                    ) : null}
                                                    <span className="truncate max-w-[160px]">
                                                        {c.name}
                                                    </span>
                                                    {c.actor ? (
                                                        <span className="text-neutral-400 text-[11px]">
                                                            · {c.actor}
                                                        </span>
                                                    ) : null}
                                                </Link>
                                                {cAllergy ? (
                                                    <AllergyBadge
                                                        note={cAllergy}
                                                        to={`${routeBase}/${projectId}/characters/${c.id}#allergy`}
                                                        testid={`image-detail-character-allergy-${c.id}`}
                                                    />
                                                ) : null}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="mt-1.5 text-neutral-400 text-sm">
                                            Unknown
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Iter-118.13 PART 2 — Notes → Look → Assignment.
                                User feedback: Look was buried below
                                Assignment, hidden inside edit. Now it sits
                                in the working context, next to the Note,
                                where the supervisor actually reads it. */}
                            <div className="order-2 rounded-xl border border-white/10 bg-card p-4">
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                        Notes
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        {entry.note ? (
                                            <FlagButton
                                                projectId={projectId}
                                                entityType="image_note"
                                                entityId={entry.id}
                                                entityLabel="image note"
                                                testid="image-detail-note-flag"
                                            />
                                        ) : null}
                                        {!editingNote ? (
                                            <button
                                                type="button"
                                                data-testid="image-detail-edit-note"
                                                onClick={startEditNote}
                                                className="tap inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-white"
                                            >
                                                <Pencil size={12} />
                                                {entry.note ? "Edit" : "Add note"}
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                                {editingNote ? (
                                    <div className="space-y-3">
                                        <Textarea
                                            data-testid="image-detail-note-input"
                                            value={noteDraft}
                                            onChange={(e) => setNoteDraft(e.target.value)}
                                            placeholder="Continuity note"
                                            rows={4}
                                            maxLength={2000}
                                            autoFocus
                                            className="bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary resize-none"
                                        />
                                        <VisibilityPicker
                                            value={visibilityDraft}
                                            onChange={setVisibilityDraft}
                                            disabled={savingNote}
                                            testidPrefix="image-detail-visibility"
                                            label="Who can see this entry?"
                                        />
                                        <div className="flex items-center gap-2 justify-end">
                                            <Button
                                                type="button"
                                                data-testid="image-detail-note-cancel"
                                                variant="secondary"
                                                onClick={cancelEditNote}
                                                disabled={savingNote}
                                                className="min-tap"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="button"
                                                data-testid="image-detail-note-save"
                                                onClick={saveNote}
                                                disabled={savingNote}
                                                className="min-tap"
                                            >
                                                {savingNote ? (
                                                    <>
                                                        <Loader2 size={14} className="mr-2 animate-spin" />
                                                        Saving…
                                                    </>
                                                ) : (
                                                    "Save note"
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                ) : entry.note ? (
                                    <p
                                        data-testid="image-detail-note"
                                        className="text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed"
                                    >
                                        {entry.note}
                                    </p>
                                ) : (
                                    <p className="text-sm italic text-neutral-500">
                                        No note added.
                                    </p>
                                )}
                            </div>

                            {/* Iter-118.13 PART 2 — Look ALWAYS rendered now
                                (even when empty) so the supervisor can
                                see at-a-glance whether a look has been
                                logged. Empty state offers a one-tap "Add
                                look" affordance that opens the same edit
                                dialog as the pencil. */}
                            <div
                                data-testid="image-detail-look"
                                className="order-3 rounded-xl border border-white/10 bg-card p-4"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                        Look
                                    </div>
                                    <button
                                        type="button"
                                        data-testid="image-detail-look-edit"
                                        onClick={() => setReassignOpen(true)}
                                        className="tap inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-white"
                                        title="Edit look"
                                    >
                                        <Pencil size={11} />
                                        {entry.look ? "Edit" : "Add look"}
                                    </button>
                                </div>
                                {entry.look ? (
                                    <div
                                        data-testid="image-detail-look-value"
                                        className="text-white text-base mt-1.5"
                                    >
                                        {entry.look}
                                    </div>
                                ) : (
                                    <p className="mt-1.5 text-sm italic text-neutral-500">
                                        No look set.
                                    </p>
                                )}
                            </div>

                            <div className="order-5 rounded-xl border border-white/10 bg-card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                        Added by
                                    </div>
                                    <div
                                        data-testid="image-detail-author"
                                        className="text-sm text-white mt-0.5"
                                    >
                                        {entry.created_by_name || "Someone"}
                                    </div>
                                    <div
                                        data-testid="image-detail-timestamp"
                                        className="text-xs text-neutral-500 mt-0.5"
                                    >
                                        {formatFullTs(entry.created_at)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {entry.media_id ? (
                                        <a
                                            data-testid="image-detail-download-original"
                                            href={mediaUrl(projectId, entry.media_id, "original", { download: true, apiBase })}
                                            // download attr triggers attachment-style save in browsers
                                            // that respect Content-Disposition (the server sets it too).
                                            download
                                            title="Download original image (full quality)"
                                            className="tap min-tap inline-flex items-center gap-2 rounded-md px-3 bg-white/5 border border-white/10 text-neutral-200 hover:bg-white/10"
                                        >
                                            <Download size={14} />
                                            <span className="text-xs font-medium">Download</span>
                                        </a>
                                    ) : null}
                                    <button
                                        data-testid="image-detail-print"
                                        type="button"
                                        onClick={() =>
                                            openPrintWindow({
                                                entry,
                                                project,
                                                scene,
                                                character,
                                                label: labelFor(entry),
                                                projectId,
                                                apiBase,
                                            })
                                        }
                                        title="Print image"
                                        className="tap min-tap inline-flex items-center gap-2 rounded-md px-3 bg-white/5 border border-white/10 text-neutral-200 hover:bg-white/10"
                                    >
                                        <Printer size={14} />
                                        <span className="text-xs font-medium">Print</span>
                                    </button>
                                    {canDelete ? (
                                        <button
                                            data-testid="image-detail-delete"
                                            onClick={() => setDeleteOpen(true)}
                                            className="tap min-tap inline-flex items-center gap-2 rounded-md px-3 bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20"
                                        >
                                            <Trash2 size={14} />
                                            <span className="text-xs font-medium">
                                                Delete
                                            </span>
                                        </button>
                                    ) : (
                                        <span
                                            data-testid="image-detail-readonly"
                                            className="text-[10px] uppercase tracking-[0.25em] text-neutral-500"
                                        >
                                            View only
                                        </span>
                                    )}
                                </div>
                            </div>
                        </section>
                    </>
                )}
            </main>

            <ConfirmDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                title="Delete this photo?"
                description="This cannot be undone. The photo and its note will be removed permanently."
                confirmLabel="Delete photo"
                cancelLabel="Keep photo"
                onConfirm={remove}
                busy={deleting}
                tone="danger"
                testidPrefix="confirm-delete-photo"
            />

            {/* iter-50: reassignment dialog reuses the create-flow
                AssignmentDialog in edit mode so the user can change
                mode (On Set ↔ Fittings), scene, character or notes
                with the same controls they used to capture the image. */}
            {entry ? (
                <AssignmentDialog
                    projectId={projectId}
                    open={reassignOpen}
                    mode="edit"
                    initialEntry={entry}
                    onClose={() => setReassignOpen(false)}
                    onSaved={async (updated) => {
                        setReassignOpen(false);
                        if (!updated) return;
                        // Iter-118 — sync flow returns the FULL grouping
                        // alongside the focal entry. If the entry we
                        // were viewing was removed (e.g. user dropped
                        // the (scene, char) pair) we navigate to a
                        // surviving sibling so the user keeps a stable
                        // page rather than landing on a 404.
                        const grouping = updated._grouping;
                        if (grouping) {
                            const survivingIds = (grouping.entries || []).map(
                                (e) => e.id,
                            );
                            if (
                                entry &&
                                !survivingIds.includes(entry.id) &&
                                grouping.entries?.length > 0
                            ) {
                                const fallback = grouping.entries[0];
                                navigate(
                                    `${routeBase}/${projectId}/state-entries/${fallback.id}`,
                                    { replace: true, state: location.state },
                                );
                                return;
                            }
                            // Entry still in grouping → take the row
                            // matching our id (may have refreshed
                            // metadata) or just the first.
                            const fresh =
                                grouping.entries?.find((e) => e.id === entry.id) ||
                                grouping.entries?.[0] ||
                                updated;
                            setEntry(fresh);
                            // Trigger the grouping useEffect to re-run.
                            setGroupingEntries(grouping.entries || []);
                        } else {
                            setEntry(updated);
                        }
                        // Refresh the legacy single-scene/single-char
                        // refs so any UI still keyed off them stays
                        // consistent.
                        try {
                            const cur = updated._grouping?.entries?.find(
                                (e) => e.id === (entry?.id),
                            ) || updated;
                            if (cur.scene_id) {
                                const { data: sc } = await api.get(
                                    `${apiBase}/${projectId}/scenes/${cur.scene_id}`,
                                );
                                setScene(sc);
                            } else {
                                setScene(null);
                            }
                            if (cur.character_id) {
                                const { data: chars } = await api.get(
                                    `${apiBase}/${projectId}/characters`,
                                );
                                setCharacter(
                                    chars.find((c) => c.id === cur.character_id) ||
                                        null,
                                );
                            }
                        } catch {
                            /* ignore — entry update has already taken effect */
                        }
                        window.dispatchEvent(
                            new CustomEvent("onsetflow:state-entry-saved"),
                        );
                    }}
                />
            ) : null}

            {/* Fullscreen lightbox — tap the image to open, X / Esc / tap
                outside to close. Same prev/next swipe so the user can
                browse a continuity strip without leaving fullscreen. */}
            {fullscreen && entry ? (
                <div
                    data-testid="image-detail-fullscreen"
                    className="fixed inset-0 z-[60] bg-black flex items-center justify-center"
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                    onClick={(e) => {
                        // Only close on backdrop tap, not on the image / arrows.
                        if (e.target === e.currentTarget) setFullscreen(false);
                    }}
                >
                    <img
                        data-testid="image-detail-fullscreen-img"
                        src={entryImageSrc(projectId, entry, "display")}
                        alt={labelFor(entry)}
                        className="max-w-full max-h-full object-contain select-none"
                        draggable={false}
                    />
                    <button
                        type="button"
                        data-testid="image-detail-fullscreen-close"
                        onClick={() => setFullscreen(false)}
                        aria-label="Close fullscreen"
                        title="Close (Esc)"
                        className="tap absolute top-4 right-4 inline-flex items-center justify-center gap-2 h-12 px-4 rounded-full bg-white text-neutral-900 hover:bg-neutral-100 font-semibold text-sm shadow-2xl ring-2 ring-black/40"
                    >
                        <X size={18} strokeWidth={2.5} />
                        <span>Close</span>
                    </button>
                    {prevId ? (
                        <button
                            type="button"
                            data-testid="image-detail-fullscreen-prev"
                            onClick={(e) => { e.stopPropagation(); goPrev(); }}
                            aria-label="Previous"
                            className="tap absolute left-3 top-1/2 -translate-y-1/2 h-14 w-14 rounded-full bg-white text-neutral-900 hover:bg-neutral-100 inline-flex items-center justify-center shadow-2xl ring-2 ring-black/40"
                        >
                            <ChevronLeft size={28} strokeWidth={2.5} />
                        </button>
                    ) : null}
                    {nextId ? (
                        <button
                            type="button"
                            data-testid="image-detail-fullscreen-next"
                            onClick={(e) => { e.stopPropagation(); goNext(); }}
                            aria-label="Next"
                            className="tap absolute right-3 top-1/2 -translate-y-1/2 h-14 w-14 rounded-full bg-white text-neutral-900 hover:bg-neutral-100 inline-flex items-center justify-center shadow-2xl ring-2 ring-black/40"
                        >
                            <ChevronRight size={28} strokeWidth={2.5} />
                        </button>
                    ) : null}
                    {total > 1 ? (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-white text-neutral-900 text-xs font-bold tabular-nums shadow-2xl ring-2 ring-black/40">
                            {position} / {total}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
